import { type FC, useEffect, useState } from 'react';
import {
    IconDay,
    IconDesktop,
    IconNight,
    IconSelectSkinny,
    IconTheme,
    Menu,
    type TestProps,
    UserBarItem,
} from '@direct-frontend/components';
import { FeatureHintTooltip } from '#shared/components/FeatureHintTooltip';
import { AUTOMATIC_PRESENTATIONS, useAutomaticPresentation } from '#shared/lib/automaticPresentation';
import { YANDEX_KIT_BRAND_NAME } from '#shared/lib/const';
import { getSafeStoreInfoRequestContext } from '#shared/lib/context';
import { FEATURE_HINT_THEME_SWITCHER_DISMISSED_KEY, usePersistentStorage } from '#shared/lib/storage';
import { setThemeMode, type ThemeMode, useThemeMode } from '#shared/lib/theme';
import { shouldShowThemeFeatureHint } from './lib/themeFeatureHint';
import {
    logThemeFeatureHintConfirm,
    logThemeFeatureHintShow,
    logThemeModeChange,
    logThemeModeShow,
} from './lib/themeLogging';

export const UserBarThemeSwitcher: FC<TestProps> = ({ 'data-testid': dataTestId = 'UserBarThemeSwitcher' }) => {
    const [isOpenMenu, setIsOpenMenu] = useState(false);
    const [isFeatureHintDismissed, setIsFeatureHintDismissed] = usePersistentStorage(
        FEATURE_HINT_THEME_SWITCHER_DISMISSED_KEY
    );
    const { mode } = useThemeMode();
    const { publishStepCompletedAt } = getSafeStoreInfoRequestContext();
    const isFeatureHintEligible = shouldShowThemeFeatureHint({
        isDismissed: Boolean(isFeatureHintDismissed),
        publishStepCompletedAt,
    });
    const {
        isActive: isFeatureHintOpen,
        isLeaving: isFeatureHintClosing,
        startLeaving: startFeatureHintClosing,
        completeLeaving: completeFeatureHintClosing,
    } = useAutomaticPresentation({
        ...AUTOMATIC_PRESENTATIONS.LOW.themeFeatureHint,
        eligibility: isFeatureHintEligible ? 'eligible' : 'ineligible',
    });

    const dismissFeatureHint = () => {
        if (startFeatureHintClosing('dismissed')) {
            setIsFeatureHintDismissed('1');
        }
    };

    useEffect(() => {
        logThemeModeShow();
    }, []);

    useEffect(() => {
        if (isFeatureHintOpen) {
            logThemeFeatureHintShow();
        }
    }, [isFeatureHintOpen]);

    const handleThemeModeChange = (nextMode: ThemeMode) => {
        setThemeMode(nextMode);
        logThemeModeChange(nextMode);
        setIsOpenMenu(false);
    };

    const handleMenuOpenChange = (isOpen: boolean) => {
        setIsOpenMenu(isOpen);

        if (isOpen && isFeatureHintOpen) {
            dismissFeatureHint();
        }
    };

    return (
        <FeatureHintTooltip
            data-testid={`${dataTestId}.FeatureHint`}
            isOpen={isFeatureHintOpen}
            isClosing={isFeatureHintClosing}
            onConfirm={logThemeFeatureHintConfirm}
            onDismiss={dismissFeatureHint}
            onCloseComplete={completeFeatureHintClosing}
            placement="left"
            title={`Тёмная тема уже в ${YANDEX_KIT_BRAND_NAME}`}
            caption="Выберите светлую, тёмную или системную тему — как вам удобнее."
        >
            <Menu
                items={[
                    {
                        value: 'light',
                        content: 'Светлая тема',
                        iconLeft: <IconDay size="24" />,
                        addonRight: mode === 'light' ? <IconSelectSkinny size="24" /> : undefined,
                        logNode: `${dataTestId}.Menu.Light`,
                        onAction: () => {
                            handleThemeModeChange('light');
                        },
                    },
                    {
                        value: 'dark',
                        content: 'Тёмная тема',
                        iconLeft: <IconNight size="24" />,
                        addonRight: mode === 'dark' ? <IconSelectSkinny size="24" /> : undefined,
                        logNode: `${dataTestId}.Menu.Dark`,
                        onAction: () => {
                            handleThemeModeChange('dark');
                        },
                    },
                    {
                        value: 'system',
                        content: 'Системная тема',
                        iconLeft: <IconDesktop size="24" />,
                        addonRight: mode === 'system' ? <IconSelectSkinny size="24" /> : undefined,
                        logNode: `${dataTestId}.Menu.System`,
                        onAction: () => {
                            handleThemeModeChange('system');
                        },
                    },
                ]}
                data-testid={`${dataTestId}.Menu`}
                size="m"
                isOpen={isOpenMenu}
                onOpenChange={handleMenuOpenChange}
                placement="left"
                trigger={<UserBarItem icon={IconTheme} data-testid={`${dataTestId}.Button`} />}
                logNode={`${dataTestId}.Menu`}
                width={240}
            />
        </FeatureHintTooltip>
    );
};
